import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  FileText, MessageSquare, Loader2, RefreshCw, Download, Search, X,
  SortAsc, SortDesc, Sparkles, Image, LayoutGrid, List,
  Database, User, Tag, Calendar, MapPin, AlertTriangle, BookOpen, Eye,
  ChevronLeft, ChevronRight
} from 'lucide-react';
import { useKB } from './KBContext';
import DocumentDetail from './DocumentDetail';

const Tooltip = ({ children, label }) => (
  <span className="kb-tooltip-wrap">
    {children}
    <span className="kb-tooltip">{label}</span>
  </span>
);

const PAGE_SIZE_OPTIONS = [24, 48, 96];

const DocumentsPanel = () => {
  const {
    documents, loadingDocs, selectedDoc, setSelectedDoc,
    viewMode, setViewMode, sortBy, setSortBy, sortOrder, setSortOrder,
    searchQuery, setSearchQuery, handleSearchSubmit, fetchDocuments,
    hasActiveFilters, clearFilters,
    activeCategories, activeYears, activeFileTypes, activeTopics,
    toggleCategory, toggleYear, toggleFileType, toggleTopic,
    handleDownload, handleView, sendQuickQuestion,
    isIngesting, handleSyncNew,
    setShowDisclaimerModal, formatFileSize, memberInfo
  } = useKB();

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(24);
  const [showAll, setShowAll] = useState(false);

  if (selectedDoc) {
    return <DocumentDetail />;
  }

  const totalDocs = documents.length;
  const totalPages = showAll ? 1 : Math.ceil(totalDocs / pageSize);
  const startIdx = showAll ? 0 : (currentPage - 1) * pageSize;
  const endIdx = showAll ? totalDocs : Math.min(startIdx + pageSize, totalDocs);
  const visibleDocs = documents.slice(startIdx, endIdx);

  const goToPage = (p) => setCurrentPage(Math.max(1, Math.min(p, totalPages)));

  const handlePageSizeChange = (val) => {
    if (val === 'all') {
      setShowAll(true);
      setCurrentPage(1);
    } else {
      setShowAll(false);
      setPageSize(Number(val));
      setCurrentPage(1);
    }
  };

  return (
    <div className="kb-docs-grid">
      <div className="kb-docs-toolbar">
        <div className="kb-docs-toolbar-left">
          <h2>Document Library</h2>
          {hasActiveFilters && (
            <button className="kb-clear-filters" onClick={clearFilters} data-testid="kb-clear-filters">
              <X size={14} /> Clear filters
            </button>
          )}
        </div>
        <div className="kb-docs-toolbar-right">
          <form onSubmit={handleSearchSubmit} className="kb-search-form">
            <Search size={16} className="kb-search-icon" />
            <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search documents..." className="kb-search-input" data-testid="kb-search-input" />
            {searchQuery && (
              <button type="button" className="kb-search-clear" onClick={() => { setSearchQuery(''); fetchDocuments({ search: '' }); }}>
                <X size={14} />
              </button>
            )}
          </form>
          <div className="kb-sort-group">
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
              className="kb-sort-select" data-testid="kb-sort-select">
              <option value="title">Name</option>
              <option value="year">Year</option>
              <option value="ingested_at">Recently Added</option>
            </select>
            <button className="kb-sort-dir" onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
              data-testid="kb-sort-dir">
              {sortOrder === 'desc' ? <SortDesc size={16} /> : <SortAsc size={16} />}
            </button>
          </div>
          <div className="kb-view-toggle" data-testid="kb-view-toggle">
            <button className={`kb-view-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')} data-testid="kb-view-grid">
              <LayoutGrid size={16} />
            </button>
            <button className={`kb-view-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')} data-testid="kb-view-list">
              <List size={16} />
            </button>
          </div>
          {/* Sync from R2 is an admin/staff ingest action — hidden from members. */}
          {!memberInfo && (
            <button className="kb-ingest-btn" onClick={handleSyncNew} disabled={isIngesting}
              data-testid="kb-ingest-main-btn">
              {isIngesting ? <><Loader2 size={16} className="kb-spin" /> Syncing...</> : <><RefreshCw size={16} /> Sync from R2</>}
            </button>
          )}
        </div>
      </div>

      {hasActiveFilters && (
        <div className="kb-active-filters" data-testid="kb-active-filters">
          {activeCategories.map(cat => (
            <span key={cat} className="kb-active-filter-pill">{cat} <X size={12} onClick={() => toggleCategory(cat)} /></span>
          ))}
          {activeYears.map(yr => (
            <span key={yr} className="kb-active-filter-pill"><Calendar size={12} /> {yr} <X size={12} onClick={() => toggleYear(yr)} /></span>
          ))}
          {activeFileTypes.map(ft => (
            <span key={ft} className="kb-active-filter-pill">{ft.toUpperCase()} <X size={12} onClick={() => toggleFileType(ft)} /></span>
          ))}
          {activeTopics.map(topic => (
            <span key={topic} className="kb-active-filter-pill"><Tag size={12} /> {topic} <X size={12} onClick={() => toggleTopic(topic)} /></span>
          ))}
          {searchQuery && <span className="kb-active-filter-pill">"{searchQuery}" <X size={12} onClick={() => { setSearchQuery(''); fetchDocuments({ search: '' }); }} /></span>}
        </div>
      )}

      {loadingDocs ? (
        <div className="kb-loading-center"><Loader2 size={32} className="kb-spin" /><p>Loading documents...</p></div>
      ) : documents.length === 0 ? (
        <div className="kb-empty-state">
          <Database size={64} />
          <h3>{hasActiveFilters ? 'No documents match your filters' : 'No documents in the knowledge base'}</h3>
          <p>{hasActiveFilters ? 'Try adjusting your filters or search query.' : (memberInfo ? 'No documents are available yet — please check back soon.' : 'Click "Sync from R2" to ingest documents.')}</p>
          {hasActiveFilters && <button className="kb-ingest-btn" onClick={clearFilters}>Clear Filters</button>}
        </div>
      ) : (
        <>
          <div className="kb-results-bar">
            <span className="kb-results-count">
              {showAll ? `${totalDocs} document${totalDocs !== 1 ? 's' : ''}` : `${startIdx + 1}–${endIdx} of ${totalDocs} documents`}
            </span>
            <div className="kb-page-size-selector">
              <span>Show:</span>
              {PAGE_SIZE_OPTIONS.map(size => (
                <button key={size}
                  className={`kb-page-size-btn ${!showAll && pageSize === size ? 'active' : ''}`}
                  onClick={() => handlePageSizeChange(size)} data-testid={`kb-page-size-${size}`}>
                  {size}
                </button>
              ))}
              <button
                className={`kb-page-size-btn ${showAll ? 'active' : ''}`}
                onClick={() => handlePageSizeChange('all')} data-testid="kb-page-size-all">
                All
              </button>
            </div>
          </div>

          {viewMode === 'grid' ? (
            <div className="kb-doc-cards">
              {visibleDocs.map(doc => (
                <motion.div key={doc.doc_id} className="kb-doc-card"
                  whileHover={{ y: -2 }} data-testid={`kb-card-${doc.doc_id}`}>
                  <div className="kb-doc-card-inner" onClick={() => setSelectedDoc(doc)}>
                    <div className="kb-doc-card-header">
                      {doc.is_asset ? <Image size={20} /> : <FileText size={20} />}
                      <div className="kb-doc-card-badges">
                        <span className={`kb-doc-card-type ${doc.is_asset ? 'asset' : ''}`}>{doc.file_type?.toUpperCase()}</span>
                        {doc.year && <span className="kb-doc-card-year">{doc.year}</span>}
                      </div>
                    </div>
                    <h3 className="kb-doc-card-title">{doc.title}</h3>
                    {doc.category && doc.category !== 'Other' && (
                      <span className="kb-doc-card-category">{doc.category}</span>
                    )}
                    {doc.conference && (
                      <span className="kb-doc-card-conference"><MapPin size={10} /> {doc.conference}</span>
                    )}
                    <p className="kb-doc-card-summary">{doc.summary}</p>
                    {doc.authors?.length > 0 && (
                      <div className="kb-doc-card-authors">
                        <User size={12} /> {doc.authors.slice(0, 2).join(', ')}{doc.authors.length > 2 ? ` +${doc.authors.length - 2}` : ''}
                      </div>
                    )}
                    <div className="kb-doc-card-tags">
                      {doc.tags?.slice(0, 3).map((tag, i) => <span key={i} className="kb-tag-small">{tag}</span>)}
                    </div>
                  </div>
                  <div className="kb-doc-card-footer">
                    <span>{formatFileSize(doc.file_size)}</span>
                    <span>{new Date(doc.ingested_at).toLocaleDateString()}</span>
                    <div className="kb-doc-card-footer-actions">
                      {!doc.is_asset && (
                        <>
                          <Tooltip label="Ask AI">
                            <button className="kb-icon-action" data-testid={`kb-action-ask-${doc.doc_id}`}
                              onClick={(e) => { e.stopPropagation(); sendQuickQuestion(`Tell me about "${doc.title}"`); }}>
                              <MessageSquare size={16} />
                            </button>
                          </Tooltip>
                          <Tooltip label="Summarize">
                            <button className="kb-icon-action" data-testid={`kb-action-summarize-${doc.doc_id}`}
                              onClick={(e) => { e.stopPropagation(); sendQuickQuestion(`Give me a detailed summary of "${doc.title}"`); }}>
                              <Sparkles size={16} />
                            </button>
                          </Tooltip>
                        </>
                      )}
                      <Tooltip label="View">
                        <button className="kb-icon-action" data-testid={`kb-action-view-${doc.doc_id}`}
                          onClick={(e) => { e.stopPropagation(); handleView(doc.r2_key); }}>
                          <Eye size={16} />
                        </button>
                      </Tooltip>
                      <Tooltip label="Download">
                        <button className="kb-icon-action" data-testid={`kb-action-download-${doc.doc_id}`}
                          onClick={(e) => { e.stopPropagation(); handleDownload(doc.r2_key); }}>
                          <Download size={16} />
                        </button>
                      </Tooltip>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="kb-doc-table" data-testid="kb-doc-table">
              <div className="kb-table-header">
                <span className="kb-th name">Name</span>
                <span className="kb-th cat">Category</span>
                <span className="kb-th conference">Conference</span>
                <span className="kb-th type">Type</span>
                <span className="kb-th year">Year</span>
                <span className="kb-th authors">Authors</span>
                <span className="kb-th size">Size</span>
                <span className="kb-th actions">Actions</span>
              </div>
              {visibleDocs.map(doc => (
                <div key={doc.doc_id} className="kb-table-row" data-testid={`kb-row-${doc.doc_id}`}>
                  <div className="kb-td name" onClick={() => setSelectedDoc(doc)}>
                    {doc.is_asset ? <Image size={16} /> : <FileText size={16} />}
                    <span className="kb-row-title">{doc.title}</span>
                  </div>
                  <span className="kb-td cat">
                    {doc.category && doc.category !== 'Other' && (
                      <span className="kb-row-cat-badge">{doc.category}</span>
                    )}
                  </span>
                  <span className="kb-td conference">
                    {doc.conference && (
                      <span className="kb-doc-card-conference"><MapPin size={10} /> {doc.conference}</span>
                    )}
                  </span>
                  <span className="kb-td type">
                    <span className={`kb-row-type-badge ${doc.is_asset ? 'asset' : ''}`}>{doc.file_type?.toUpperCase()}</span>
                  </span>
                  <span className="kb-td year">{doc.year || '—'}</span>
                  <span className="kb-td authors">{doc.authors?.slice(0, 2).join(', ') || '—'}</span>
                  <span className="kb-td size">{formatFileSize(doc.file_size)}</span>
                  <div className="kb-td actions">
                    {!doc.is_asset && (
                      <Tooltip label="Ask AI">
                        <button className="kb-row-action"
                          onClick={() => sendQuickQuestion(`Tell me about "${doc.title}"`)}>
                          <MessageSquare size={16} />
                        </button>
                      </Tooltip>
                    )}
                    <Tooltip label="View">
                      <button className="kb-row-action"
                        onClick={() => handleView(doc.r2_key)}>
                        <Eye size={16} />
                      </button>
                    </Tooltip>
                    <Tooltip label="Download">
                      <button className="kb-row-action"
                        onClick={() => handleDownload(doc.r2_key)}>
                        <Download size={16} />
                      </button>
                    </Tooltip>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {!showAll && totalPages > 1 && (
            <div className="kb-pagination" data-testid="kb-pagination">
              <button className="kb-page-btn" onClick={() => goToPage(currentPage - 1)} disabled={currentPage <= 1}
                data-testid="kb-page-prev">
                <ChevronLeft size={16} /> Previous
              </button>
              <div className="kb-page-numbers">
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                  .reduce((acc, p, i, arr) => {
                    if (i > 0 && p - arr[i - 1] > 1) acc.push('...');
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, i) =>
                    p === '...' ? (
                      <span key={`dots-${i}`} className="kb-page-dots">...</span>
                    ) : (
                      <button key={p} className={`kb-page-num ${p === currentPage ? 'active' : ''}`}
                        onClick={() => goToPage(p)} data-testid={`kb-page-${p}`}>
                        {p}
                      </button>
                    )
                  )}
              </div>
              <button className="kb-page-btn" onClick={() => goToPage(currentPage + 1)} disabled={currentPage >= totalPages}
                data-testid="kb-page-next">
                Next <ChevronRight size={16} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DocumentsPanel;
